/**
 * OsHealthTiles — J6
 *
 * 14 colour-coded tiles rendering the system health signals from
 * GET /api/v1/observability/health. Each tile drills through to its
 * evidence (filtered work-item list, metric chart, learning feed, etc.)
 * — drill-through routes are wired in `DRILL_TARGETS` below.
 *
 * Status palette:
 *   ok            → success (green)
 *   warning       → warning (amber)
 *   critical      → error  (red)
 *   informational → muted  (neutral)
 *
 * PRD §9.10.1 + §12.3.9 J6 frontend page #4.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { t } from '@/lib/i18n';
import {
  Activity,
  AlertTriangle,
  Clock,
  ListChecks,
  Repeat,
  TrendingUp,
  GitBranch,
  Layers,
  Users,
  Lightbulb,
  Waves,
  Gauge,
} from 'lucide-react';
import type { RorosHealthSignalDTO } from '@/services/roros/control-tower.service';

// ─── Drill-through targets ───────────────────────────────────────────────────
//
// Each signal points to a route that surfaces the underlying evidence. Where
// no dedicated evidence page exists yet, we point at the closest extant page
// (Control Tower, Decisions, Learning) so the click is never a no-op.

const DRILL_TARGETS: Record<string, string> = {
  ftt_system: '/roros/quality',
  ftt_domain: '/roros/quality',
  cycle_time: '/roros/control-tower',
  wip_utilization: '/roros/control-tower',
  escalation_rate: '/roros/control-tower?status=escalated',
  routine_coverage: '/roros/artifacts/routines',
  improvement_closure: '/roros/artifacts/learning',
  decision_latency: '/roros/decisions',
  maturity_distribution: '/roros/artifacts/routines',
  decision_method_dist: '/roros/decisions',
  decision_impact_dist: '/roros/decisions',
  advisory_consultation_rate: '/roros/decisions',
  learning_source_dist: '/roros/artifacts/learning',
  mura_indicator: '/roros/control-tower',
};

// ─── Icons per signal ────────────────────────────────────────────────────────

const SIGNAL_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  ftt_system: Gauge,
  ftt_domain: Gauge,
  cycle_time: Clock,
  wip_utilization: Activity,
  escalation_rate: AlertTriangle,
  routine_coverage: Repeat,
  improvement_closure: TrendingUp,
  decision_latency: GitBranch,
  maturity_distribution: Layers,
  decision_method_dist: ListChecks,
  decision_impact_dist: ListChecks,
  advisory_consultation_rate: Users,
  learning_source_dist: Lightbulb,
  mura_indicator: Waves,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<RorosHealthSignalDTO['status'], { bar: string; label: string; badge: 'success' | 'warning' | 'error' | 'default' }> = {
  ok: { bar: 'bg-success-500', label: 'text-success-700', badge: 'success' },
  warning: { bar: 'bg-warning-500', label: 'text-warning-700', badge: 'warning' },
  critical: { bar: 'bg-error-500', label: 'text-error-700', badge: 'error' },
  informational: { bar: 'bg-muted-foreground/40', label: 'text-muted-foreground', badge: 'default' },
};

function formatValue(signal: RorosHealthSignalDTO): string {
  const v = signal.value;
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') {
    return signal.unit === '%' ? `${v.toFixed(1)}%` : `${v}${signal.unit ? ' ' + signal.unit : ''}`;
  }
  // Distribution buckets — show top key/value pair
  const entries = Object.entries(v);
  if (entries.length === 0) return '—';
  const top = entries.sort((a, b) => Number(b[1]) - Number(a[1]))[0];
  return `${top[0]}: ${top[1]}`;
}

// ─── Component ──────────────────────────────────────────────────────────────

export interface OsHealthTilesProps {
  signals: RorosHealthSignalDTO[];
  /** Optional title rendered above the grid (omit to render bare grid). */
  title?: string;
  /** When true, hides drill-through navigation (used in print/PDF preview). */
  noDrill?: boolean;
}

export function OsHealthTiles({ signals, title, noDrill }: OsHealthTilesProps) {
  const navigate = useNavigate();

  const handleActivate = (signal: RorosHealthSignalDTO) => {
    if (noDrill) return;
    const target = DRILL_TARGETS[signal.key];
    if (target) navigate(target);
  };

  return (
    <section aria-label={t('OS Health Signals')} data-testid="road-section-os-health">
      {title && (
        <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
          <Activity className="w-5 h-5" aria-hidden />
          {title}
        </h2>
      )}
      <div
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3"
        role="list"
        aria-label={t('OS Health Signals')}
        data-testid="os-health-tiles"
      >
        {signals.map((signal) => {
          const Icon = SIGNAL_ICONS[signal.key] ?? Activity;
          const style = STATUS_STYLES[signal.status] ?? STATUS_STYLES.informational;
          const interactive = !noDrill && !!DRILL_TARGETS[signal.key];

          return (
            <Card
              key={signal.key}
              padding="sm"
              role="listitem"
              tabIndex={interactive ? 0 : -1}
              aria-label={`${signal.label} — ${formatValue(signal)} (${signal.status})`}
              data-testid={`os-health-tile-${signal.key}`}
              data-signal-status={signal.status}
              onClick={() => interactive && handleActivate(signal)}
              onKeyDown={(e) => {
                if (interactive && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleActivate(signal);
                }
              }}
              className={cn(
                'relative overflow-hidden flex flex-col items-start gap-1.5',
                interactive && 'cursor-pointer hover:border-primary-300 transition-colors',
              )}
            >
              <span className={cn('absolute top-0 left-0 h-1 w-full', style.bar)} aria-hidden />
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Icon className="w-3.5 h-3.5" aria-hidden />
                <span className="line-clamp-1">{signal.label}</span>
              </div>
              <div className={cn('text-lg font-semibold', style.label)}>{formatValue(signal)}</div>
              <Badge variant={style.badge} className="text-[10px] uppercase tracking-wide">
                {t(signal.status)}
              </Badge>
            </Card>
          );
        })}
      </div>
    </section>
  );
}
